import i18next from "i18next";

// NOTE: The concern name strings inside each category array below (e.g. "Fat
// Reduction", "Acne") are match keys compared against the backend-provided
// `DisplayedConcern.name` field (see utils/concernGrouping.ts and
// utils/skincareConcerns.ts) — they are never rendered directly from this
// file, so they are intentionally left untranslated. Only the category
// *titles* (the object keys) are user-facing display text; see
// `getConcernCategoryTitle` below for their translation.
export const CONCERN_CATEGORIES = {
  "Body Contours": [
    "Fat Reduction",
    "Cellulite",
    "Body Sculpting",
    "Muscle Tone",
    "Bra Fat",
    "Stubborn Fat",
    "Lack of Muscle Definition",
    "Aging Hands",
    "Hip Dips",
  ],
  "Dental Aesthetics": ["Teeth Whitening", "Dental Aesthetics"],
  "Facial Contours": [
    "Chin & Jawline",
    "Double Chin",
    "Gummy Smile",
    "Smile",
    "Facial Contours",
    "Droopy Eyebrows",
    "Nasolabial Folds",
    "Sunken Temples",
    "Lip Enhancements",
    "Cheek Enhancements",
    "Facial Balancing",
  ],
  "Hair": ["Hair Loss", "Hair Removal"],
  "Health": [
    "Sick Visit",
    "Immune Support",
    "Mold Toxicity",
    "Nutrition",
    "Acute Illness",
    "Heavy Metal Toxicity",
    "Weight Management",
    "Pediatric Health",
    "Women's Health",
    "Men's Health",
    "Neuropathy",
    "Allergies",
    "Numbness & Tingling",
    "Nausea",
  ],
  "Mental Health": [
    "Mental Health",
    "Insomnia",
    "Anxiety",
    "Depression",
    "Stress Relief",
    "Postpartum Recovery",
    "Psychosomatic & Trauma",
  ],
  "Oral Health": ["Tooth Pain", "Dental Wellness", "Oral Hygiene"],
  "Pain Relief": [
    "Chronic Pain",
    "Inflammation",
    "Discomfort",
    "Migraines",
    "Headaches",
    "Disc Herniation (Bulge)",
    "Plantar Fasciitis",
    "Tennis Elbow",
    "Golfer's Elbow",
    "Knee Pain",
    "Acute Pain",
    "Shoulder Pain",
    "Pinched Nerve",
    "Radiculopathy",
    "Frozen Shoulder",
    "Migraine Relief",
    "Sciatica",
    "Joint Pain",
    "Arthritis",
  ],
  "Skin Clarity": [
    "Acne",
    "Discoloration",
    "Redness",
    "Spider Veins",
    "Stretch Marks",
    "Hyperpigmentation",
    "Melasma",
    "Rosacea",
    "Age Spots",
    "Dark Spots",
    "Tattoo Removal",
    "Sun Damage",
    "Dull Skin",
    "Uneven Skin Tone",
    "Broken Capillaries",
  ],
  "Skin Texture": [
    "Enlarged Pores",
    "Loose Skin",
    "Scars",
    "Skin Rejuvenation",
    "Wrinkles",
    "Anti-aging",
    "Dry Skin",
    "Moles",
    "Varicose Veins",
    "Acne Scars",
    "Sagging Skin",
    "Fine Lines",
    "Cherry Angiomas",
    "Prejuvenation",
    "Volume Loss",
    "Skin Elasticity",
    "Collagen Stimulation",
    "Marionette Lines",
    "Smile Lines",
  ],
  "Wellness": [
    "Sexual Wellness",
    "Wellness",
    "Sleep",
    "Hormone Imbalance",
    "Detox",
    "Athletic Recovery",
    "Longevity",
    "Weight Loss",
    "Erectile Dysfunction",
    "Fatigue",
    "Hydration",
    "Low Libido",
    "Low Testosterone",
    "Sexual Dysfunction",
    "Vaginal Dryness",
    "Urinary Incontinence",
    "Skin Irritation",
    "Vaginal Elasticity",
    "Gut Health",
    "Excessive Sweating",
    "Ingrown Hair",
  ],
} as const;

export const GENDER_FILTERED_CONCERNS = {
  "Women's Health": { hiddenIf: "male" },
  "Postpartum Recovery": { hiddenIf: "male" },
  "Men's Health": { hiddenIf: "female" },
  "Erectile Dysfunction": { hiddenIf: "female" },
  "Vaginal Dryness": { hiddenIf: "male" },
  "Vaginal Elasticity": { hiddenIf: "male" },
} as const;

export type ConcernCategoryName = keyof typeof CONCERN_CATEGORIES;
export type GenderFilterRule =
  (typeof GENDER_FILTERED_CONCERNS)[keyof typeof GENDER_FILTERED_CONCERNS];

const CONCERN_CATEGORY_TRANSLATION_KEYS: Record<ConcernCategoryName, string> = {
  "Body Contours": "concernCategories.bodyContours",
  "Dental Aesthetics": "concernCategories.dentalAesthetics",
  "Facial Contours": "concernCategories.facialContours",
  "Hair": "concernCategories.hair",
  "Health": "concernCategories.health",
  "Mental Health": "concernCategories.mentalHealth",
  "Oral Health": "concernCategories.oralHealth",
  "Pain Relief": "concernCategories.painRelief",
  "Skin Clarity": "concernCategories.skinClarity",
  "Skin Texture": "concernCategories.skinTexture",
  "Wellness": "concernCategories.wellness",
};

/** Translated, user-facing display title for a concern category name. */
export function getConcernCategoryTitle(categoryName: ConcernCategoryName): string {
  return i18next.t(CONCERN_CATEGORY_TRANSLATION_KEYS[categoryName]);
}
